// ── Mint Strategy Wizard ──────────────────────────────────────────────
function MintStrategyPage({ onToast, onNavigate }) {
  const [step, setStep]       = React.useState(1);
  const [template, setTpl]    = React.useState(null);
  const [name, setName]       = React.useState("");
  const [market, setMarket]   = React.useState("BTC/USDT");
  const [period, setPeriod]   = React.useState("4h");

  const TEMPLATES = [
    { id:"trend",     name:"Trend following", desc:"EMA/MA crossovers, ride momentum", icon:TrendingUp,  color:"#11ff99" },
    { id:"meanrev",   name:"Mean reversion",  desc:"RSI/Bollinger, fade extremes",    icon:Activity,    color:"#3b9eff" },
    { id:"momentum",  name:"Momentum",        desc:"Rate-of-change, breakout entries", icon:Cpu,         color:"#3b9eff" },
    { id:"pairs",     name:"Pairs trading",   desc:"Statistical arbitrage on pairs",  icon:GitMerge,    color:"#D946EF" },
    { id:"vol",       name:"Volatility",      desc:"ATR/IV plays, options-style risk", icon:Shield,      color:"#11ff99" },
    { id:"custom",    name:"Blank canvas",    desc:"Start from scratch in Designer",  icon:Layers,      color:"var(--muted)" },
  ];

  const MARKETS = ["BTC/USDT","ETH/USDT","SOL/USDT","XAU/USD","XAG/USD","SPX","NDX","Custom"];
  const PERIODS = ["1m","5m","15m","1h","4h","1D","1W"];

  const next = () => setStep(s => s+1);
  const back = () => setStep(s => Math.max(1, s-1));

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const submit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) { onToast?.("Please enter a strategy name."); return; }
    setSubmitting(true);
    try {
      setError(null);
      const sb = window.sbClient;
      const { data: { user } } = await sb.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const templateSpec = {
        trend:   { indicators:[{id:'ef',type:'ema',source:'close',params:{period:9}},{id:'es',type:'ema',source:'close',params:{period:21}},{id:'rsi',type:'rsi',source:'close',params:{period:14}}], entry:{long:['ef > es and rsi > 50']}, exit:{long:['ef < es']} },
        meanrev: { indicators:[{id:'rsi',type:'rsi',source:'close',params:{period:14}},{id:'bb',type:'bbands',source:'close',params:{period:20,k:2}}], entry:{long:['rsi < 35']}, exit:{long:['rsi > 65']} },
        momentum:{ indicators:[{id:'ef',type:'ema',source:'close',params:{period:12}},{id:'roc',type:'rsi',source:'close',params:{period:14}}], entry:{long:['ef > ef']}, exit:{long:['roc < 50']} },
        pairs:   { indicators:[{id:'rsi',type:'rsi',source:'close',params:{period:14}}], entry:{long:['rsi > 50']}, exit:{long:['rsi < 50']} },
        vol:     { indicators:[{id:'atr',type:'atr',source:'close',params:{period:14}},{id:'bb',type:'bbands',source:'close',params:{period:20,k:2}}], entry:{long:['atr > 0']}, exit:{long:['atr < 0']} },
        custom:  { indicators:[], entry:{long:[]}, exit:{long:[]} },
      };
      const spec = templateSpec[template || 'custom'] || templateSpec.custom;
      const { data, error } = await sb.from('strategies').insert({
        user_id: user.id,
        name: trimmedName,
        description: document.querySelector('textarea[data-mint-desc]')?.value?.trim() || '',
        status: 'saved',
        asset: market,
        timeframe: period,
        canvas_state: { nodes: [], edges: [] },
        current_params: spec,
        performance_metrics: {},
      }).select('id').single();
      if (error) throw error;
      // Set as current strategy in pipeline state
      window.GQ?.state?.setCurrentStrategy?.({ id: data.id, name: trimmedName });
      window.gqTrack?.('mint_strategy', { template: template || 'custom', market, period });
      onToast?.(`Created "${trimmedName}" — opening Strategy Lab`);
      setTimeout(() => onNavigate?.("designer"), 400);
    } catch (err) {
      const msg = "Couldn't create strategy: " + (err.message || 'unknown error');
      setError(msg);
      onToast?.(msg);
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20, maxWidth:840, margin:"0 auto", width:"100%" }}>
      <PageHdr
        icon={Plus} iconColor="#11ff99" iconBg="rgba(17,255,153,0.10)"
        title="Mint a new strategy"
        subtitle="Choose a template, name it, and we'll set up the rest"
      />

      {/* Stepper */}
      <div style={{ display:"flex", gap:12, marginBottom:8 }}>
        {[1,2,3].map(n => (
          <div key={n} style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ height:3, background: step>=n ? "#11ff99" : "var(--border-strong)", borderRadius:"var(--r-full)" }}/>
            <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color: step>=n?"#11ff99":"var(--muted)" }}>
              Step {n} · {["Template","Details","Confirm"][n-1]}
            </div>
          </div>
        ))}
      </div>

      {step === 1 && (
        <div>
          <div style={{ fontFamily:"var(--font-display)", fontSize:26, color:"var(--ink)", marginBottom:6, letterSpacing:"-0.018em" }}>Pick a template</div>
          <div style={{ fontFamily:"var(--font-ui)", fontSize:14, color:"var(--muted)", marginBottom:24 }}>Every template seeds the Designer with sensible defaults.</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:10 }}>
            {TEMPLATES.map(t => {
              const Ic = t.icon;
              const selected = template === t.id;
              return (
                <button key={t.id} onClick={()=>setTpl(t.id)} style={{
                  background: selected ? "rgba(17,255,153,0.06)" : "var(--bg-card)",
                  border:`1px solid ${selected ? "#11ff99" : "var(--border)"}`,
                  borderRadius:"var(--r-lg)", padding:18, textAlign:"left", cursor:"pointer", transition:"all .15s",
                }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:`${t.color}18`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:12 }}>
                    <Ic size={15} color={t.color}/>
                  </div>
                  <div style={{ fontFamily:"var(--font-ui)", fontSize:14, fontWeight:600, color:"var(--ink)", marginBottom:4 }}>{t.name}</div>
                  <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)", lineHeight:1.45 }}>{t.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card">
          <div style={{ fontFamily:"var(--font-display)", fontSize:24, color:"var(--ink)", marginBottom:18, letterSpacing:"-0.018em" }}>Strategy details</div>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)", marginBottom:5 }}>Strategy name</div>
              <input className="input" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. BTC 4h Momentum"/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div>
                <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)", marginBottom:5 }}>Market</div>
                <select className="input" value={market} onChange={e=>setMarket(e.target.value)}>
                  {MARKETS.map(m=><option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)", marginBottom:5 }}>Time period</div>
                <select className="input" value={period} onChange={e=>setPeriod(e.target.value)}>
                  {PERIODS.map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)", marginBottom:5 }}>Description (optional)</div>
              <textarea className="input" data-mint-desc rows={3} placeholder="What's the thesis?" style={{ resize:"vertical" }}/>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card">
          <div style={{ fontFamily:"var(--font-display)", fontSize:24, color:"var(--ink)", marginBottom:18, letterSpacing:"-0.018em" }}>Confirm</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {[
              ["Template", TEMPLATES.find(t=>t.id===template)?.name || "—"],
              ["Name",     name || "Untitled"],
              ["Market",   market],
              ["Period",   period],
            ].map(([l,v]) => (
              <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:"1px solid var(--border)" }}>
                <span style={{ fontFamily:"var(--font-ui)", fontSize:13, color:"var(--muted)" }}>{l}</span>
                <span style={{ fontFamily:"var(--font-mono)", fontSize:13, color:"var(--ink)" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div style={{
          background: "rgba(255,32,71,0.06)", border: "1px solid rgba(255,32,71,0.25)",
          borderRadius: "var(--r-md)", padding: "8px 12px", marginBottom: 12,
          fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--red)",
        }}>{error}</div>
      )}

      <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
        <button className="btn btn-ghost btn-sm" onClick={back} disabled={step===1} style={{ opacity:step===1?0.3:1 }}>← Back</button>
        {step < 3
          ? <button className="btn btn-primary btn-sm" onClick={next} disabled={step===1 && !template} style={{ opacity:step===1&&!template?0.4:1 }}>Continue →</button>
          : <button className="btn btn-primary btn-sm" onClick={submit} disabled={submitting} style={{opacity:submitting?0.7:1}}>{submitting ? "Creating…" : "Mint & open in Designer →"}</button>}
      </div>
    </div>
  );
}

window.MintStrategyPage = MintStrategyPage;
